/** Formatowanie dat, liczb i tekstów — polska lokalizacja, jak na mockupach. */

const MONTHS_SHORT = ['STY', 'LUT', 'MAR', 'KWI', 'MAJ', 'CZE', 'LIP', 'SIE', 'WRZ', 'PAŹ', 'LIS', 'GRU']

export const EMPTY = '—'

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** 21.08.2026 */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${day}.${month}.${d.getFullYear()}`
}

/** 21.08 — krótki zapis używany w kolumnach terminów */
export function formatDateShort(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
}

/** { month: 'SIE', day: '21' } — kafelek daty przy terminach */
export function dateBadge(value: string | number | Date | null | undefined): { month: string; day: string } {
  const d = toDate(value)
  if (!d) return { month: EMPTY, day: EMPTY }
  return { month: MONTHS_SHORT[d.getMonth()] ?? EMPTY, day: String(d.getDate()).padStart(2, '0') }
}

/** Poniedziałek, 10 sierpnia 2026 */
export function formatDateLong(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  const text = new Intl.DateTimeFormat('pl-PL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function formatDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  const time = new Intl.DateTimeFormat('pl-PL', { hour: '2-digit', minute: '2-digit' }).format(d)
  return `${formatDate(d)}, ${time}`
}

/** Liczba dni od dziś (ujemna = przeszłość). */
export function daysUntil(value: string | number | Date | null | undefined): number | null {
  const d = toDate(value)
  if (!d) return null
  const today = new Date()
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((a - b) / 86_400_000)
}

/** "za 11 dni" / "dziś" / "2 dni temu" */
export function formatDueLabel(value: string | number | Date | null | undefined): string {
  const days = daysUntil(value)
  if (days === null) return EMPTY
  if (days === 0) return 'dziś'
  if (days === 1) return 'jutro'
  if (days === -1) return 'wczoraj'
  if (days > 0) return `za ${days} ${plural(days, 'dzień', 'dni', 'dni')}`
  return `${-days} ${plural(-days, 'dzień', 'dni', 'dni')} temu`
}

/** "2 godz. temu", "wczoraj", "4 dni temu" — jak w kolumnie AKTYWNOŚĆ */
export function formatRelative(value: string | number | Date | null | undefined): string {
  const d = toDate(value)
  if (!d) return EMPTY
  const diffMs = Date.now() - d.getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'przed chwilą'
  if (minutes < 60) return `${minutes} min temu`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} godz. temu`
  const days = Math.round(hours / 24)
  if (days === 1) return 'wczoraj'
  if (days < 7) return `${days} dni temu`
  if (days < 31) {
    const weeks = Math.round(days / 7)
    return weeks === 1 ? 'tydzień temu' : `${weeks} tyg. temu`
  }
  const months = Math.round(days / 30)
  return months <= 1 ? 'miesiąc temu' : `${months} mies. temu`
}

/** Odmiana rzeczownika: plural(5, 'sprawa', 'sprawy', 'spraw') → 'spraw' */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n)
  if (abs === 1) return one
  const last = abs % 10
  const lastTwo = abs % 100
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return few
  return many
}

export function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY
  return new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value)
}

/** 2 480 000,00 zł */
export function formatMoney(value: number | string | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : value
  if (n === null || n === undefined || Number.isNaN(n)) return EMPTY
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(n)
}

/** 0.94 lub 94 → "94%" */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY
  const pct = value <= 1 ? value * 100 : value
  return `${Math.round(pct)}%`
}

/** "Anna Wiśniewska" → "AW" */
export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.charAt(0) ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : ''
  return (first + last).toUpperCase()
}

/** Wartość albo "—" (nigdy pusty string w UI). */
export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY
  return String(value)
}
